/**
 * RecordPaymentModal — Phase C.2 (Feb 2026)
 *
 * Records a payment by **dollar amount** against an invoice. Companion
 * modal to the line-item-level `InvoicePaymentModal` — this one is for
 * accounting workflows where the user wants to record an arbitrary
 * partial payment (e.g. client wired $1,200 against an open balance)
 * without selecting individual orders.
 *
 * Source of truth: the `payments` collection (Phase C). Status is
 * derived server-side; this modal just POSTs the payment and refreshes.
 *
 * Endpoints:
 *   POST /api/pricing/billing/invoice/{id}/payments
 *   GET  /api/pricing/billing/invoice/{id}/payments  (history)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Loader2, Receipt, History, ArrowRight } from 'lucide-react';
import { formatDateTimeLocal } from '../../../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAID_TOLERANCE = 0.01;

const formatCurrency = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return formatDateTimeLocal(iso);
  } catch {
    return iso;
  }
};

const todayISO = () => new Date().toISOString().split('T')[0];

const StatusPill = ({ status }) => {
  const map = {
    invoiced:        { bg: 'bg-blue-50',    text: 'text-blue-700',    label: 'Invoiced' },
    partially_paid:  { bg: 'bg-amber-50',   text: 'text-amber-700',   label: 'Partially Paid' },
    paid:            { bg: 'bg-emerald-50', text: 'text-emerald-700', label: 'Paid' },
  };
  const cfg = map[status] || { bg: 'bg-slate-100', text: 'text-slate-600', label: status || '—' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

export const RecordPaymentModal = ({ open, onOpenChange, invoice, onPaymentApplied }) => {
  const [history, setHistory] = useState([]);
  const [historyState, setHistoryState] = useState({ paid_amount: 0, balance_amount: 0, status: 'invoiced', payment_count: 0 });
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Derived
  const totalAmount = Number(invoice?.total_amount) || 0;
  // Prefer the freshly-fetched history state (in case the row was stale)
  const paidAmount = Number(historyState.paid_amount) || Number(invoice?.paid_amount) || 0;
  const balanceAmount = Number(historyState.balance_amount) || Number(invoice?.balance_amount) || (totalAmount - paidAmount);
  const status = historyState.status || invoice?.status;
  
  const numericAmount = useMemo(() => {
    const n = Number(amount);
    return Number.isFinite(n) ? n : NaN;
  }, [amount]);
  
  const validation = useMemo(() => {
    if (amount === '' || amount === null) return { valid: false, error: null };
    if (!Number.isFinite(numericAmount)) return { valid: false, error: 'Enter a numeric amount.' };
    if (numericAmount <= 0) return { valid: false, error: 'Payment amount must be greater than 0.' };
    if (numericAmount > balanceAmount + PAID_TOLERANCE) {
      return {
        valid: false,
        error: `Payment cannot exceed current balance (${formatCurrency(balanceAmount)}).`,
      };
    }
    if (!paymentDate) return { valid: false, error: 'Payment date is required.' };
    return { valid: true, error: null };
  }, [amount, numericAmount, balanceAmount, paymentDate]);
  
  const fetchHistory = useCallback(async () => {
    if (!invoice?.id) return;
    setIsLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/pricing/billing/invoice/${invoice.id}/payments`);
      setHistory(res.data?.payments || []);
      setHistoryState({
        paid_amount: res.data?.paid_amount ?? 0,
        balance_amount: res.data?.balance_amount ?? 0,
        status: res.data?.status,
        payment_count: res.data?.payment_count ?? 0,
      });
    } catch (err) {
      console.error('Error fetching payment history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [invoice?.id]);
  
  useEffect(() => {
    if (open && invoice?.id) {
      // Reset form on open
      setAmount('');
      setPaymentDate(todayISO());
      setPaymentReference('');
      setNotes('');
      setShowHistory(false);
      fetchHistory();
    }
  }, [open, invoice?.id, fetchHistory]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validation.valid) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/pricing/billing/invoice/${invoice.id}/payments`,
        {
          amount: numericAmount,
          payment_date: new Date(paymentDate).toISOString(),
          payment_reference: paymentReference.trim() || null,
          notes: notes.trim() || null,
        },
      );
      const newStatus = res.data?.invoice?.status || status;
      const newBalance = res.data?.invoice?.balance_amount ?? (balanceAmount - numericAmount);
      toast.success(
        newStatus === 'paid'
          ? `Invoice ${invoice.invoice_number} fully paid.`
          : `Recorded ${formatCurrency(numericAmount)} — balance ${formatCurrency(newBalance)}.`,
      );
      onPaymentApplied?.();
      onOpenChange(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to record payment';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!invoice) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="record-payment-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" />
            Record Payment
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Record a payment by dollar amount. Status updates automatically from the payments collection.
          </DialogDescription>
        </DialogHeader>
        
        {/* Invoice context strip */}
        <div className="bg-slate-50 rounded-lg border border-slate-200 px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Invoice</p>
            <p className="font-mono font-semibold text-slate-900">{invoice.invoice_number}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Client</p>
            <p className="font-semibold text-slate-900 truncate" title={invoice.client_name || invoice.client_code}>
              {invoice.client_name || invoice.client_code || '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Status</p>
            <StatusPill status={status} />
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Total</p>
            <p className="font-bold tabular-nums text-slate-900">{formatCurrency(totalAmount)}</p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Paid</p>
            <p className="font-bold tabular-nums text-emerald-700" data-testid="modal-paid-amount">
              {formatCurrency(paidAmount)}
            </p>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 font-medium">Balance</p>
            <p className="font-bold tabular-nums text-amber-700" data-testid="modal-balance-amount">
              {formatCurrency(balanceAmount)}
            </p>
          </div>
        </div>
        
        {/* Payment form */}
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="record-payment-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="rp-amount" className="text-xs font-medium text-slate-700">
                Payment Amount <span className="text-red-500">*</span>
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input
                  id="rp-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="pl-6 h-9 tabular-nums"
                  data-testid="rp-amount-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 mt-1.5">
                <button
                  type="button"
                  onClick={() => setAmount(balanceAmount.toFixed(2))}
                  disabled={balanceAmount <= 0}
                  className="text-[11px] text-indigo-600 hover:text-indigo-700 hover:underline disabled:opacity-40"
                  data-testid="rp-fill-balance-btn"
                >
                  Pay full balance ({formatCurrency(balanceAmount)})
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="rp-date" className="text-xs font-medium text-slate-700">
                Payment Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="rp-date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="h-9 mt-1"
                data-testid="rp-date-input"
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="rp-reference" className="text-xs font-medium text-slate-700">
              Payment Reference <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="rp-reference"
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Check #, wire ref, transaction ID..."
              className="h-9 mt-1"
              data-testid="rp-reference-input"
              maxLength={100}
            />
          </div>
          
          <div>
            <Label htmlFor="rp-notes" className="text-xs font-medium text-slate-700">
              Notes <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="rp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes..."
              className="mt-1 text-sm"
              rows={2}
              data-testid="rp-notes-input"
              maxLength={2000}
            />
          </div>
          
          {validation.error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="rp-validation-error">
              {validation.error}
            </div>
          )}
          
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
              data-testid="rp-toggle-history-btn"
            >
              <History className="w-3.5 h-3.5" />
              {showHistory ? 'Hide' : 'View'} payment history
              {historyState.payment_count > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                  {historyState.payment_count}
                </span>
              )}
            </button>
            
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                data-testid="rp-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!validation.valid || isSubmitting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="rp-submit-btn"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1.5" />}
                Record {amount && validation.valid ? formatCurrency(numericAmount) : 'Payment'}
              </Button>
            </div>
          </div>
        </form>
        
        {/* Payment history (collapsed by default) */}
        {showHistory && (
          <div className="border-t border-slate-100 pt-3" data-testid="rp-history-section">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Payment History</h4>
            {isLoadingHistory ? (
              <div className="text-center py-4">
                <Loader2 className="w-4 h-4 mx-auto animate-spin text-slate-400" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No payments recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="tf-thead">
                    <tr>
                      <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Date</th>
                      <th className="text-right px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Amount</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Reference</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Notes</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Recorded By</th>
                      <th className="text-left px-2 py-1.5 text-[10px] font-semibold text-slate-600 uppercase">Logged At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.slice().reverse().map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 last:border-0" data-testid={`rp-history-row-${p.id}`}>
                        <td className="px-2 py-1.5 text-slate-700">{formatDateTime(p.payment_date)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-emerald-700">{formatCurrency(p.amount)}</td>
                        <td className="px-2 py-1.5 text-slate-600 font-mono">{p.payment_reference || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-500 truncate max-w-[160px]" title={p.notes || ''}>{p.notes || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-500">{p.recorded_by_name || p.recorded_by || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-400">{formatDateTime(p.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentModal;
